import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import {
  BookmarkBorder,
  Bookmark,
  Lock as LockIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { getCardImageUrl, getArtworkTitle } from '../utils/artworkApi';
import { formatCatalogNumber } from '../utils/catalogFormatters';
import { buildArtworkDetailLink } from '../utils/artworkDetailLink';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { ThumbnailImage } from './ThumbnailImage';
import type { ArtworkItem } from './search/artworkItem';

interface SearchResultsListProps {
  artworks: ArtworkItem[];
  isBookmarked: (id: string) => boolean;
  toggleBookmark: (id: string) => void;
  currentUserId: string | null;
  canEdit?: (artworkId: string, ownerId: string) => boolean;
}

export const SearchResultsList = ({
  artworks,
  isBookmarked,
  toggleBookmark,
  currentUserId,
  canEdit,
}: SearchResultsListProps) => {
  const theme = useTheme();
  const { isDelegateOf } = useActiveCatalog();

  if (artworks.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="search-results-list"
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      {artworks.map((artworkItem, index) => {
        const thumbnailUrl = artworkItem.coverImageUrl || getCardImageUrl(artworkItem);
        const title = getArtworkTitle(artworkItem);
        const creator = artworkItem.creator || 'Unknown Artist';
        const catalogId = formatCatalogNumber(artworkItem);
        const userCanEdit = canEdit
          ? canEdit(artworkItem.artworkId, artworkItem.userId)
          : false;
        const isOwn = currentUserId !== null && artworkItem.userId === currentUserId;
        const isPrivate = isOwn && artworkItem.visibility === 'private';
        const detailLink = buildArtworkDetailLink(
          { artworkId: artworkItem.artworkId, userId: artworkItem.userId },
          { currentUserId, isDelegateOf }
        );

        return (
          <Box
            key={artworkItem.artworkId}
            sx={{
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: '68px 1fr auto',
              gap: 2,
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1.5,
              transition: 'box-shadow 150ms ease-in-out',
              '&:hover': { boxShadow: 2 },
            }}
          >
            <Box
              component={Link}
              to={detailLink.to}
              state={{
                itemIds: artworks.map((a) => a.artworkId),
                currentIndex: index,
                routePrefix: detailLink.routePrefix,
                from: 'search',
              }}
              sx={{
                width: 68,
                height: 68,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <ThumbnailImage src={thumbnailUrl} alt={title} />
            </Box>

            <Box
              component={Link}
              to={detailLink.to}
              state={{
                itemIds: artworks.map((a) => a.artworkId),
                currentIndex: index,
                routePrefix: detailLink.routePrefix,
                from: 'search',
              }}
              sx={{
                minWidth: 0,
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontFamily: 'serif',
                  fontStyle: 'italic',
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  lineHeight: 1.25,
                  color: 'primary.main',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {creator}
              </Typography>
              {catalogId && (
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'text.disabled',
                  }}
                >
                  {catalogId}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isPrivate && (
                <LockIcon
                  data-testid="visibility-icon-private"
                  sx={{ fontSize: 18, color: 'text.secondary' }}
                />
              )}
              {userCanEdit && (
                <Tooltip title="Edit Object">
                  <IconButton
                    component={Link}
                    to={`/upload-object?edit=${artworkItem.artworkId}`}
                    aria-label="Edit"
                    size="small"
                    color="primary"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                aria-label={`Bookmark ${title}`}
                aria-pressed={isBookmarked(artworkItem.artworkId)}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleBookmark(artworkItem.artworkId);
                }}
                sx={{
                  color: isBookmarked(artworkItem.artworkId)
                    ? 'primary.main'
                    : 'text.secondary',
                }}
              >
                {isBookmarked(artworkItem.artworkId) ? (
                  <Bookmark fontSize="small" />
                ) : (
                  <BookmarkBorder fontSize="small" />
                )}
              </IconButton>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
